import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Button,
  Typography,
  IconButton,
  CircularProgress
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { addToCart, updateCartItem, removeCartItem } from '../services/api';
import { useCart } from '../context/CartContext';

function Cart() {
  const { cartItems, updateCart, handleRemoveItem } = useCart();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const hasLoadedRef = useRef(false);

  useEffect(() => {
    const loadCart = async () => {
      if (hasLoadedRef.current) return;
      try {
        setLoading(true);
        await updateCart();
        setError(null);
      } catch (error) {
        // Only sets error if it's not a "cart empty" response
        if (error.response?.status !== 404) {
          setError('Failed to load cart items');
          console.error('Cart loading error:', error);
        }
      } finally {
        setLoading(false);
        hasLoadedRef.current = true;
      }
    };
    loadCart();
  }, [updateCart]);


  const updateQuantity = async (productId, newQuantity) => {
    try {
      console.log(`Updating product ${productId} to quantity ${newQuantity}`);

      // Call the API function
      await updateCartItem(productId, newQuantity);

      // Refresh cart data after update
      await updateCart();
    } catch (error) {
      console.error('Error updating quantity:', error);
      setError('Failed to update cart');
    }
  };


  if (loading) {
    return (
      <Container sx={{ mt: 4, textAlign: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }


  if (!cartItems || cartItems.length === 0) {
    return (
      <Container sx={{ mt: 4 }}>
        <Typography variant="h4" gutterBottom>Shopping Cart</Typography>
        <Typography>Your cart is empty</Typography>
      </Container>
    );
  }


  if (error) {
    return (
      <Container sx={{ mt: 4 }}>
        <Typography color="error">{error}</Typography>
      </Container>
    );
  }

  const total = cartItems ? cartItems.reduce((sum, item) =>
    sum + (item.product?.price || 0 ) * item.quantity, 0
  ) : 0;


  return (
    <Container sx={{ mt: 4 }}>
      <Typography variant="h4" gutterBottom>Shopping Cart</Typography>
      {!cartItems || cartItems.length === 0 ? (
        <Typography>Your cart is empty</Typography>
      ) : (
        <>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Product</TableCell>
                <TableCell>Price</TableCell>
                <TableCell>Quantity</TableCell>
                <TableCell>Total</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {cartItems.map((item) => (
                <TableRow key={item.id}>
                  <TableCell>{item.product?.name || 'Unknown product'}</TableCell>
                  <TableCell>₦{(item.product?.price || 0).toLocaleString()}</TableCell>
                  <TableCell>
                    <Button
                      onClick={() => updateQuantity(item.product.id, item.quantity - 1)}
                      disabled={item.quantity <= 1}
                    >-</Button>
                    {item.quantity}
                    <Button
                      onClick={() => updateQuantity(item.product.id, item.quantity + 1)}
                    >+</Button>
                    </TableCell>
                    <TableCell>₦{((item.product?.price || 0) * item.quantity).toLocaleString()}</TableCell>
                    <TableCell>
                      <IconButton onClick={() => handleRemoveItem(item.product.id)}>
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <Typography variant="h6" sx={{ mt: 2 }}>
            Total: ₦{total.toLocaleString()}
          </Typography>
          <Button
            variant="contained"
            sx={{ mt: 2 }}
            onClick={() => navigate('/checkout')}
          >
            Proceed to Checkout
          </Button>
        </>
      )}
    </Container>
  );
}

export default Cart;